// Subjects for one org programme — arrive as subject-group rows with
// `children`, rendered as optgroups (v3 useSubjectSelect parity). Moved from
// pages/groups/useGroupOptions (extract-on-second-consumer: groups filters + form).
// `programmeSubjectIdBySubjectId` maps each org-instance subject id to its
// catalog `programme_subject_id` (v3 subject onChange captured both — the
// library scope persists the catalog id for syllabus content-area tags).
import { useList } from '@refinedev/core';
import { useMemo } from 'react';

import type { APMultiSelectOptionGroup } from '@/components/ap/form';
import { attrs, type JsonApiRow } from './jsonApi';

export const useSubjectGroupSelect = (programmeId: string | number | undefined) => {
  const { data, isLoading } = useList({
    resource: 'subjects',
    filters: [
      { field: 'by_org_programme_id', operator: 'eq', value: programmeId },
      { field: 'unarchived', operator: 'eq', value: true },
    ],
    pagination: { pageSize: 40 },
    queryOptions: { enabled: !!programmeId },
  });

  const { groups, programmeSubjectIdBySubjectId } = useMemo(() => {
    const idMap = new Map<string, number | null>();
    const groupList: APMultiSelectOptionGroup[] = (data?.data ?? [])
      .map((row: JsonApiRow) => {
        const item = attrs(row);
        const children: any[] = item.children ?? [];
        return {
          label: String(item.label ?? ''),
          options: children.map((child) => {
            idMap.set(String(child.id), child.programme_subject_id ?? null);
            return {
              value: child.id,
              label: String(child.label ?? child.id),
            };
          }),
        };
      })
      .filter((group) => group.options.length > 0);
    return { groups: groupList, programmeSubjectIdBySubjectId: idMap };
  }, [data?.data]);

  return { groups, programmeSubjectIdBySubjectId, isLoading };
};
